import { useMutation, useQueryClient } from "@tanstack/react-query";

import { callLcosService } from "../../data/lcosServices";
import { useAccessControl } from "../../app/use-access-control";
import { useAccessToken } from "../use-access-token";
import { inquiryQueryKeys } from "../use-inquiries/query-keys";
import { familyQueryKeys } from "./query-keys";
import type { FamilyContactDetailWithId, FamilyInfo, FamilyRelationship } from "./types";
import { useFamilyConstants } from "./use-family-constants";
import { addFamilyRole } from "./use-family-roles";
import { FAMILY_ROLE_INTERNAL_CODES, findFamilyRoleIdByInternalCode } from "./family-role-utils";

export const useFamilyRelationships = (familyId?: string) => {
  const { getToken } = useAccessToken();
  const queryClient = useQueryClient();

  const addFamilyRelationshipMutator = useMutation({
    mutationFn: async (payload: FamilyRelationship) =>
      familyId ? addFamilyRelationship(await getToken(), familyId, payload) : undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyQueryKeys.base });
    }
  });

  return {
    addFamilyRelationshipMutator
  };
};

export const useReplaceContact = () => {
  const { getToken } = useAccessToken();
  const { activeCenterId } = useAccessControl();
  const { EMERGENCY_CONTACT_VALUE, roles } = useFamilyConstants();
  const primaryContactRoleId =
    findFamilyRoleIdByInternalCode(roles, FAMILY_ROLE_INTERNAL_CODES.PRIMARY_CONTACT, { activeOnly: true }) ??
    findFamilyRoleIdByInternalCode(roles, FAMILY_ROLE_INTERNAL_CODES.PRIMARY_CONTACT) ??
    "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      familyId,
      relationshipId,
      studentId,
      oldContact,
      newContact
    }: {
      familyId: string;
      relationshipId: string;
      studentId: number;
      oldContact: FamilyInfo;
      newContact: FamilyContactDetailWithId;
    }) =>
      replaceFamilyRelationship(
        await getToken(),
        activeCenterId,
        familyId,
        relationshipId,
        studentId,
        EMERGENCY_CONTACT_VALUE,
        primaryContactRoleId,
        oldContact,
        newContact
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyQueryKeys.base });
      queryClient.invalidateQueries({ queryKey: inquiryQueryKeys.baseDashboard() });
    }
  });
};

const replaceFamilyRelationship = async (
  accessToken: string,
  centerId: string,
  familyId: string,
  relationshipId: string,
  studentId: number,
  emergencyContactValue: string,
  primaryContactRoleId: string,
  oldContact: FamilyInfo,
  newContact: FamilyContactDetailWithId
) => {
  await addFamilyRelationship(accessToken, familyId, {
    subjectContactId: newContact.id,
    objectContactId: studentId,
    relationshipId: relationshipId
  });

  if (oldContact.isPrimaryContact) {
    if (!primaryContactRoleId) {
      throw new Error("Unable to resolve PRIMARY_CONTACT role id from family constants");
    }
    await addFamilyRole(accessToken, centerId, familyId, {
      id: newContact.id,
      role: primaryContactRoleId
    });
  }

  const emergencyContactRelationship = oldContact.relationships.find(
    ({ relationship: { value } }) => value === emergencyContactValue
  );
  if (emergencyContactRelationship) {
    await addFamilyRelationship(accessToken, familyId, {
      subjectContactId: newContact.id,
      objectContactId: studentId,
      relationshipId: emergencyContactValue
    });
  }
};

export const addFamilyRelationship = (accessToken: string, familyId: string, payload: FamilyRelationship) => {
  return callLcosService<FamilyRelationship>(accessToken, `/api/families/${familyId}/relationships`, "POST", payload);
};
